import { BLOG_POST_BY_SLUG_GROQ, BLOG_POSTS_LIST_GROQ, blog_query_now } from "./queries"
import {
  createSanityBrowserClient,
  getSanityDataset,
  getSanityProjectId,
  SANITY_API_VERSION,
  use_sanity_dev_proxy,
} from "./sanity_client"
import type { BlogPostDetail, BlogPostListItem } from "./types"

/** Matches `@sanity/client` encodeQueryString for GET /data/query (see node_modules/@sanity/client). */
function encode_sanity_groq_querystring(
  query: string,
  params: Record<string, unknown> | undefined,
): string {
  const searchParams = new URLSearchParams()
  searchParams.append("query", query)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        searchParams.append(`$${key}`, JSON.stringify(value))
      }
    }
  }
  searchParams.append("returnQuery", "false")
  return `?${searchParams.toString()}`
}

async function groq_fetch_via_dev_proxy<T>(query: string, params?: Record<string, unknown>): Promise<T> {
  const projectId = getSanityProjectId()
  const dataset = getSanityDataset()
  if (!projectId || !dataset) {
    throw new Error("Missing VITE_SANITY_PROJECT_ID or VITE_SANITY_DATASET")
  }
  const qs = encode_sanity_groq_querystring(query, params)
  const path = `/sanity-proxy/v${SANITY_API_VERSION}/data/query/${dataset}${qs}`
  const res = await fetch(path)
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Sanity proxy ${res.status}: ${text.slice(0, 200)}`)
  }
  const json = (await res.json()) as { result?: T }
  return json.result as T
}

export async function fetch_blog_posts(): Promise<BlogPostListItem[]> {
  const client = createSanityBrowserClient()
  if (!client) {
    return []
  }
  const params = { now: blog_query_now() }
  const rows = use_sanity_dev_proxy()
    ? await groq_fetch_via_dev_proxy<BlogPostListItem[]>(BLOG_POSTS_LIST_GROQ, params)
    : await client.fetch<BlogPostListItem[]>(BLOG_POSTS_LIST_GROQ, params)
  return rows ?? []
}

export async function fetch_blog_post_by_slug(slug: string): Promise<BlogPostDetail | null> {
  const client = createSanityBrowserClient()
  if (!client) {
    return null
  }
  const params = { slug, now: blog_query_now() }
  const row = use_sanity_dev_proxy()
    ? await groq_fetch_via_dev_proxy<BlogPostDetail | null>(BLOG_POST_BY_SLUG_GROQ, params)
    : await client.fetch<BlogPostDetail | null>(BLOG_POST_BY_SLUG_GROQ, params)
  return row
}
